export const LOCALES = ["ko", "en"] as const;
export type Locale = (typeof LOCALES)[number];

const DEFAULT_LOCALE: Locale = "ko";
const ENGLISH_LOCALE: Locale = "en";

const LOCALE_SET = new Set<string>(LOCALES);

function isLocale(value: string | undefined): value is Locale {
  return Boolean(value && LOCALE_SET.has(value));
}

function getLocalePrefix(locale: Locale): "" | "/en" {
  return locale === "en" ? "/en" : "";
}

function normalizePath(pathname: string): string {
  const [pathOnly = "/"] = pathname.split(/[?#]/, 1);
  const withSlash = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, "") : "/";
}

export function stripLocalePrefix(pathname: string): {
  locale: Locale;
  pathname: string;
  prefixed: boolean;
} {
  const normalized = normalizePath(pathname);
  if (normalized === "/en") {
    return { locale: "en", pathname: "/", prefixed: true };
  }
  if (normalized.startsWith("/en/")) {
    return {
      locale: "en",
      pathname: normalizePath(normalized.slice(3)),
      prefixed: true,
    };
  }
  return { locale: DEFAULT_LOCALE, pathname: normalized, prefixed: false };
}

export function toLocalizedPath(pathname: string, locale: Locale): string {
  const stripped = stripLocalePrefix(pathname);
  const basePath = stripped.pathname;
  if (locale === DEFAULT_LOCALE) return basePath;
  if (basePath === "/") return getLocalePrefix(locale);
  return `${getLocalePrefix(locale)}${basePath}`;
}

function parseAcceptLanguage(acceptLanguage: string | null): string[] {
  if (!acceptLanguage) return [];
  return acceptLanguage
    .split(",")
    .map((part) => {
      const [tag = "", qValue] = part.trim().split(";q=");
      const q = qValue ? Number(qValue) : 1;
      return { tag: tag.toLowerCase(), q: Number.isFinite(q) ? q : 0 };
    })
    .filter(({ tag, q }) => tag.length > 0 && q > 0)
    .sort((a, b) => b.q - a.q)
    .map(({ tag }) => tag);
}

export function detectPreferredLocale(input: {
  acceptLanguage: string | null;
  cookieLocale?: string;
}): Locale {
  if (isLocale(input.cookieLocale)) return input.cookieLocale;

  for (const tag of parseAcceptLanguage(input.acceptLanguage)) {
    const language = tag.split("-")[0];
    if (language === "en") return "en";
    if (language === "ko") return "ko";
  }

  return DEFAULT_LOCALE;
}

export function buildLanguageAlternates(pathname: string): {
  "ko-KR": string;
  en: string;
  "x-default": string;
} {
  return {
    "ko-KR": toLocalizedPath(pathname, "ko"),
    en: toLocalizedPath(pathname, "en"),
    "x-default": toLocalizedPath(pathname, "ko"),
  };
}

export function toAbsoluteUrl(siteUrl: string, pathname: string): string {
  const base = siteUrl.replace(/\/+$/, "");
  const path = normalizePath(pathname);
  return `${base}${path === "/" ? "" : path}`;
}

export function buildAbsoluteLanguageAlternates(
  siteUrl: string,
  pathname: string,
): {
  "ko-KR": string;
  en: string;
  "x-default": string;
} {
  const alternates = buildLanguageAlternates(pathname);
  return {
    "ko-KR": toAbsoluteUrl(siteUrl, alternates["ko-KR"]),
    en: toAbsoluteUrl(siteUrl, alternates.en),
    "x-default": toAbsoluteUrl(siteUrl, alternates["x-default"]),
  };
}
